<!-- RothConversionWizard.vue - Complete File -->
<template>
  <div class="roth-conversion-wizard">
    <header class="wizard-header">
      <div class="header-content">
        <h1>🔄 Roth Conversion Analysis</h1>
        <div class="step-indicator">
          <div 
            v-for="(step, index) in steps" 
            :key="index"
            class="step-dot"
            :class="{ 
              'active': currentStep === index, 
              'completed': currentStep > index 
            }"
            @click="goToStep(index)"
          >
            <span class="step-number">{{ index + 1 }}</span>
            <span class="step-label">{{ step.label }}</span>
          </div>
        </div>
      </div>
    </header>

    <!-- Quick Decision Summary (always visible after step 1) -->
    <div v-if="currentStep > 0" class="quick-summary">
      <div class="summary-card" :class="decisionClass">
        <div class="decision-result">
          <div class="decision-icon">{{ decisionIcon }}</div>
          <div class="decision-content">
            <h3>{{ decisionTitle }}</h3>
            <p>{{ decisionSummary }}</p>
          </div>
        </div>
        <div class="key-metrics">
          <div class="metric">
            <span class="metric-label">Lifetime Benefit</span>
            <span class="metric-value" :class="{ 'positive': totalBenefit > 0, 'negative': totalBenefit <= 0 }">
              {{ formatCurrency(totalBenefit) }}
            </span>
          </div>
          <div class="metric">
            <span class="metric-label">Break-even</span>
            <span class="metric-value">{{ breakEvenYears }} years</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Step Content -->
    <div class="wizard-content">
      <transition name="step-transition" mode="out-in">
        <!-- Step 1: Basic Inputs -->
        <div v-if="currentStep === 0" key="inputs" class="wizard-step">
          <div class="step-header">
            <h2>📊 Basic Information</h2>
            <p>Let's start with your current financial situation and retirement timeline.</p>
          </div>

          <div class="inputs-grid">
            <!-- Account Balances Section -->
            <div class="input-section">
              <h3>Current Retirement Accounts</h3>
              <div class="input-group">
                <label for="current-balance">Traditional IRA/401(k) Balance</label>
                <div class="input-wrapper">
                  <span class="input-prefix">$</span>
                  <input
                    id="current-balance"
                    v-model.number="localCurrentBalance"
                    type="number"
                    class="form-input"
                    step="10000"
                    min="0"
                    placeholder="500,000"
                    @input="updateParameter('currentBalance', $event.target.value)"
                  />
                </div>
                <small class="input-help">Total balance in tax-deferred accounts available for conversion</small>
              </div>

              <div class="input-group">
                <label for="conversion-amount">Annual Conversion Amount</label>
                <div class="input-wrapper">
                  <span class="input-prefix">$</span>
                  <input
                    id="conversion-amount"
                    v-model.number="localConversionAmount"
                    type="number"
                    class="form-input"
                    step="5000"
                    min="0"
                    :max="localCurrentBalance"
                    placeholder="50,000"
                    @input="updateParameter('conversionAmount', $event.target.value)"
                  />
                </div>
                <small class="input-help">Amount you're considering converting annually</small>
              </div>
            </div>

            <!-- Income Information Section -->
            <div class="input-section">
              <h3>Income Information</h3>
              <div class="input-group">
                <label for="current-income">Current Annual Income</label>
                <div class="input-wrapper">
                  <span class="input-prefix">$</span>
                  <input
                    id="current-income"
                    v-model.number="localCurrentIncome"
                    type="number"
                    class="form-input"
                    step="5000"
                    min="0"
                    placeholder="120,000"
                    @input="updateParameter('currentIncome', $event.target.value)"
                  />
                </div>
                <small class="input-help">Your current taxable income before conversion</small>
              </div>

              <div class="input-group">
                <label for="retirement-income">Expected Retirement Income</label>
                <div class="input-wrapper">
                  <span class="input-prefix">$</span>
                  <input
                    id="retirement-income"
                    v-model.number="localRetirementIncome"
                    type="number"
                    class="form-input"
                    step="5000"
                    min="0"
                    placeholder="80,000"
                    @input="updateParameter('retirementIncome', $event.target.value)"
                  />
                </div>
                <small class="input-help">Annual income in retirement (excluding Roth withdrawals)</small>
              </div>
            </div>

            <!-- Personal Timeline Section -->
            <div class="input-section">
              <h3>Personal Timeline</h3>
              <div class="input-row">
                <div class="input-group">
                  <label for="current-age">Current Age</label>
                  <input
                    id="current-age"
                    v-model.number="localCurrentAge"
                    type="number"
                    class="form-input"
                    min="18"
                    max="100"
                    placeholder="45"
                    @input="updateParameter('currentAge', $event.target.value)"
                  />
                </div>

                <div class="input-group">
                  <label for="retirement-age">Retirement Age</label>
                  <input
                    id="retirement-age"
                    v-model.number="localRetirementAge"
                    type="number"
                    class="form-input"
                    min="50"
                    max="80"
                    placeholder="65"
                    @input="updateParameter('retirementAge', $event.target.value)"
                  />
                </div>

                <div class="input-group">
                  <label for="life-expectancy">Life Expectancy</label>
                  <input
                    id="life-expectancy"
                    v-model.number="localLifeExpectancy"
                    type="number"
                    class="form-input"
                    min="65"
                    max="120"
                    placeholder="90"
                    @input="updateParameter('lifeExpectancy', $event.target.value)"
                  />
                </div>
              </div>
            </div>

            <!-- Tax Situation Section -->
            <div class="input-section">
              <h3>Tax Situation</h3>
              <div class="input-group">
                <label for="filing-status">Filing Status</label>
                <select
                  id="filing-status"
                  v-model="localFilingStatus"
                  class="form-select"
                  @change="updateParameter('filingStatus', $event.target.value)"
                >
                  <option value="single">Single</option>
                  <option value="marriedJoint">Married Filing Jointly</option>
                  <option value="marriedSeparate">Married Filing Separately</option>
                  <option value="headOfHousehold">Head of Household</option>
                </select>
              </div>

              <div class="input-row">
                <div class="input-group">
                  <label for="current-state">Current State</label>
                  <select
                    id="current-state"
                    v-model="localCurrentState"
                    class="form-select"
                    @change="updateParameter('currentState', $event.target.value)"
                  >
                    <option value="NY">New York</option>
                    <option value="CA">California</option>
                    <option value="FL">Florida (No Income Tax)</option>
                    <option value="TX">Texas (No Income Tax)</option>
                  </select>
                </div>

                <div class="input-group">
                  <label for="retirement-state">Retirement State</label>
                  <select
                    id="retirement-state"
                    v-model="localRetirementState"
                    class="form-select"
                    @change="updateParameter('retirementState', $event.target.value)"
                  >
                    <option value="NY">New York</option>
                    <option value="CA">California</option>
                    <option value="FL">Florida (No Income Tax)</option>
                    <option value="TX">Texas (No Income Tax)</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 2: Tax Analysis -->
        <div v-else-if="currentStep === 1" key="analysis" class="wizard-step">
          <div class="step-header">
            <h2>📊 Tax Analysis</h2>
            <p>Understanding your current vs. future tax situation.</p>
          </div>

          <div class="tax-analysis-grid">
            <div class="tax-card current">
              <h3>Current Tax Situation</h3>
              <div class="tax-details">
                <div class="tax-item">
                  <span class="tax-label">Current Income:</span>
                  <span class="tax-value">{{ formatCurrency(localCurrentIncome) }}</span>
                </div>
                <div class="tax-item">
                  <span class="tax-label">Estimated Marginal Rate:</span>
                  <span class="tax-value">{{ formatPercentage(0.24) }}</span>
                </div>
                <div class="tax-item">
                  <span class="tax-label">Estimated Effective Rate:</span>
                  <span class="tax-value">{{ formatPercentage(0.18) }}</span>
                </div>
              </div>
            </div>

            <div class="tax-card retirement">
              <h3>Retirement Tax Situation</h3>
              <div class="tax-details">
                <div class="tax-item">
                  <span class="tax-label">Retirement Income:</span>
                  <span class="tax-value">{{ formatCurrency(localRetirementIncome) }}</span>
                </div>
                <div class="tax-item">
                  <span class="tax-label">Expected Marginal Rate:</span>
                  <span class="tax-value">{{ formatPercentage(0.22) }}</span>
                </div>
                <div class="tax-item">
                  <span class="tax-label">Expected Effective Rate:</span>
                  <span class="tax-value">{{ formatPercentage(0.16) }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="conversion-preview">
            <h3>Conversion Preview</h3>
            <div class="preview-metrics">
              <div class="preview-item">
                <span class="preview-label">Conversion Amount:</span>
                <span class="preview-value">{{ formatCurrency(localConversionAmount) }}</span>
              </div>
              <div class="preview-item">
                <span class="preview-label">Estimated Tax Cost:</span>
                <span class="preview-value">{{ formatCurrency(estimatedTaxCost) }}</span>
              </div>
              <div class="preview-item">
                <span class="preview-label">After-Tax Cost:</span>
                <span class="preview-value">{{ formatCurrency(localConversionAmount + estimatedTaxCost) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 3: Results -->
        <div v-else-if="currentStep === 2" key="results" class="wizard-step">
          <div class="step-header">
            <h2>📈 Analysis Results</h2>
            <p>Your personalized Roth conversion recommendation.</p>
          </div>

          <div class="results-content">
            <div class="recommendation-card" :class="recommendationClass">
              <div class="recommendation-header">
                <div class="recommendation-icon">{{ recommendationIcon }}</div>
                <div class="recommendation-text">
                  <h3>{{ recommendationTitle }}</h3>
                  <p>{{ recommendationDescription }}</p>
                </div>
              </div>
              
              <div class="recommendation-details">
                <div class="detail-item">
                  <span class="detail-label">Estimated Lifetime Benefit:</span>
                  <span class="detail-value" :class="lifetimeBenefitClass">{{ formatCurrency(estimatedLifetimeBenefit) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Break-even Period:</span>
                  <span class="detail-value">{{ estimatedBreakEven }} years</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Tax Cost Today:</span>
                  <span class="detail-value">{{ formatCurrency(estimatedTaxCost) }}</span>
                </div>
              </div>
            </div>

            <div class="action-items">
              <h4>Next Steps</h4>
              <div class="action-list">
                <div v-for="(action, index) in recommendedActions" :key="index" class="action-item">
                  <span class="action-number">{{ index + 1 }}</span>
                  <span class="action-text">{{ action }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- Navigation -->
    <div class="wizard-navigation">
      <button 
        v-if="currentStep > 0"
        @click="prevStep"
        class="btn btn-secondary"
      >
        ← Previous
      </button>
      
      <div class="nav-spacer"></div>
      
      <button 
        v-if="currentStep < steps.length - 1"
        @click="nextStep"
        class="btn btn-primary"
        :disabled="!canProceed"
      >
        Next →
      </button>
      
      <button 
        v-if="currentStep === steps.length - 1"
        @click="$emit('close')"
        class="btn btn-success"
      >
        Complete Analysis
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';

export default {
  name: 'RothConversionWizard',
  props: {
    formatCurrency: {
      type: Function,
      required: true
    },
    personalInfo: {
      type: Object,
      default: () => ({})
    },
    assumptions: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['close'],
  setup(props) {
    const currentStep = ref(0);
    
    const steps = [
      { label: 'Basic Info', icon: '📊' },
      { label: 'Tax Analysis', icon: '💰' },
      { label: 'Results', icon: '📈' }
    ];

    // Local state for form inputs
    const localCurrentBalance = ref(500000);
    const localConversionAmount = ref(50000);
    const localCurrentIncome = ref(120000);
    const localRetirementIncome = ref(80000);
    const localCurrentAge = ref(45);
    const localRetirementAge = ref(65);
    const localLifeExpectancy = ref(90);
    const localFilingStatus = ref('single');
    const localCurrentState = ref('NY');
    const localRetirementState = ref('FL');

    // Initialize from props if available
    if (props.personalInfo?.primary) {
      const primary = props.personalInfo.primary;
      if (primary.dateOfBirth) {
        const birthYear = new Date(primary.dateOfBirth).getFullYear();
        localCurrentAge.value = new Date().getFullYear() - birthYear;
      }
      if (primary.targetRetirementAge) {
        localRetirementAge.value = primary.targetRetirementAge;
      }
      if (primary.lifeExpectancy) {
        localLifeExpectancy.value = primary.lifeExpectancy;
      }
    }

    // Computed properties for analysis
    const estimatedTaxCost = computed(() => {
      return localConversionAmount.value * 0.24; // Simplified 24% rate
    });

    const estimatedLifetimeBenefit = computed(() => {
      const currentRate = 0.24;
      const retirementRate = 0.22;
      const rateDifference = retirementRate - currentRate;
      
      if (rateDifference <= 0) {
        return localConversionAmount.value * rateDifference * 0.5; // Negative benefit
      }
      
      return localConversionAmount.value * rateDifference * 1.5; // Positive benefit with growth
    });

    const estimatedBreakEven = computed(() => {
      if (estimatedLifetimeBenefit.value <= 0) return 'Never';
      
      const yearsToRetirement = localRetirementAge.value - localCurrentAge.value;
      return Math.max(5, Math.min(yearsToRetirement - 5, 15));
    });

    const lifetimeBenefitClass = computed(() => {
      return estimatedLifetimeBenefit.value > 0 ? 'positive' : 'negative';
    });

    const recommendationClass = computed(() => {
      if (estimatedLifetimeBenefit.value > 20000) return 'recommendation-good';
      if (estimatedLifetimeBenefit.value > -10000) return 'recommendation-maybe';
      return 'recommendation-bad';
    });

    const recommendationIcon = computed(() => {
      if (estimatedLifetimeBenefit.value > 20000) return '🚀';
      if (estimatedLifetimeBenefit.value > -10000) return '⚖️';
      return '🛑';
    });

    const recommendationTitle = computed(() => {
      if (estimatedLifetimeBenefit.value > 20000) return 'Strong Recommendation: Proceed with Conversions';
      if (estimatedLifetimeBenefit.value > -10000) return 'Consider Small Conversions';
      return 'Conversions Not Recommended';
    });

    const recommendationDescription = computed(() => {
      if (estimatedLifetimeBenefit.value > 20000) {
        return 'Based on your tax situation, Roth conversions appear to provide significant long-term benefits.';
      } else if (estimatedLifetimeBenefit.value > -10000) {
        return 'Conversions may provide modest benefits. Consider starting with smaller amounts.';
      } else {
        return 'Your current tax rate appears higher than expected retirement rates, making conversions costly.';
      }
    });

    const recommendedActions = computed(() => {
      if (estimatedLifetimeBenefit.value > 20000) {
        return [
          `Start with a ${props.formatCurrency(Math.min(localConversionAmount.value, 25000))} conversion this year`,
          'Set aside cash outside retirement accounts to pay taxes',
          'Consider spreading conversions over multiple years',
          'Consult with your tax advisor about timing'
        ];
      } else if (estimatedLifetimeBenefit.value > -10000) {
        return [
          'Consider small conversions in low-income years',
          'Focus on staying within your current tax bracket',
          'Explore other tax-efficient strategies first',
          'Monitor for changes in your tax situation'
        ];
      } else {
        return [
          'Focus on maximizing traditional retirement contributions',
          'Consider tax-loss harvesting in taxable accounts',
          'Explore other tax-efficient investment strategies',
          'Revisit Roth conversions if your situation changes'
        ];
      }
    });

    // Summary metrics for header
    const totalBenefit = computed(() => estimatedLifetimeBenefit.value);
    const breakEvenYears = computed(() => {
      const years = estimatedBreakEven.value;
      return typeof years === 'number' ? years : 99;
    });

    const decisionClass = computed(() => recommendationClass.value);
    const decisionIcon = computed(() => recommendationIcon.value);
    const decisionTitle = computed(() => recommendationTitle.value);
    const decisionSummary = computed(() => recommendationDescription.value);

    const canProceed = computed(() => {
      switch (currentStep.value) {
        case 0:
          return localCurrentBalance.value > 0 && 
                 localCurrentAge.value > 0 && 
                 localRetirementAge.value > localCurrentAge.value;
        case 1:
          return true;
        default:
          return true;
      }
    });

    // Navigation methods
    const nextStep = () => {
      if (currentStep.value < steps.length - 1 && canProceed.value) {
        currentStep.value++;
      }
    };

    const prevStep = () => {
      if (currentStep.value > 0) {
        currentStep.value--;
      }
    };

    const goToStep = (stepIndex) => {
      if (stepIndex <= currentStep.value || (stepIndex === currentStep.value + 1 && canProceed.value)) {
        currentStep.value = stepIndex;
      }
    };

    // Update methods
    const updateParameter = (field, value) => {
      const numValue = Number(value) || 0;
      
      switch (field) {
        case 'currentBalance':
          localCurrentBalance.value = numValue;
          break;
        case 'conversionAmount':
          localConversionAmount.value = numValue;
          break;
        case 'currentIncome':
          localCurrentIncome.value = numValue;
          break;
        case 'retirementIncome':
          localRetirementIncome.value = numValue;
          break;
        case 'currentAge':
          localCurrentAge.value = numValue;
          break;
        case 'retirementAge':
          localRetirementAge.value = numValue;
          break;
        case 'lifeExpectancy':
          localLifeExpectancy.value = numValue;
          break;
        case 'filingStatus':
          localFilingStatus.value = value;
          break;
        case 'currentState':
          localCurrentState.value = value;
          break;
        case 'retirementState':
          localRetirementState.value = value;
          break;
      }
    };

    const formatPercentage = (value) => {
      return `${(value * 100).toFixed(1)}%`;
    };

    return {
      currentStep,
      steps,
      localCurrentBalance,
      localConversionAmount,
      localCurrentIncome,
      localRetirementIncome,
      localCurrentAge,
      localRetirementAge,
      localLifeExpectancy,
      localFilingStatus,
      localCurrentState,
      localRetirementState,
      estimatedTaxCost,
      estimatedLifetimeBenefit,
      estimatedBreakEven,
      lifetimeBenefitClass,
      recommendationClass,
      recommendationIcon,
      recommendationTitle,
      recommendationDescription,
      recommendedActions,
      totalBenefit,
      breakEvenYears,
      decisionClass,
      decisionIcon,
      decisionTitle,
      decisionSummary,
      canProceed,
      nextStep,
      prevStep,
      goToStep,
      updateParameter,
      formatPercentage
    };
  }
};
</script>

<style scoped>
.roth-conversion-wizard {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
}

.wizard-header {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
  padding: 2rem;
}

.header-content h1 {
  margin: 0 0 2rem 0;
  color: #2c3e50;
  font-size: 2rem;
  text-align: center;
}

.step-indicator {
  display: flex;
  justify-content: center;
  gap: 2rem;
}

.step-dot {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 1rem;
  border-radius: 8px;
}

.step-dot:hover {
  background: #f8f9fa;
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e9ecef;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  transition: all 0.3s ease;
}

.step-dot.active .step-number {
  background: #007bff;
  color: white;
}

.step-dot.completed .step-number {
  background: #28a745;
  color: white;
}

.step-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: #6c757d;
}

.step-dot.active .step-label {
  color: #007bff;
  font-weight: 600;
}

/* Quick Summary */
.quick-summary {
  margin-bottom: 2rem;
}

.summary-card {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.recommendation-good {
  border-left: 4px solid #28a745;
  background: linear-gradient(to right, #f8fff9, white);
}

.recommendation-maybe {
  border-left: 4px solid #ffc107;
  background: linear-gradient(to right, #fffbf0, white);
}

.recommendation-bad {
  border-left: 4px solid #dc3545;
  background: linear-gradient(to right, #fff5f5, white);
}

.decision-result {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.decision-icon {
  font-size: 2rem;
}

.decision-content h3 {
  margin: 0 0 0.5rem 0;
  color: #2c3e50;
  font-size: 1.25rem;
}

.decision-content p {
  margin: 0;
  color: #6c757d;
  line-height: 1.4;
}

.key-metrics {
  display: flex;
  gap: 2rem;
}

.metric {
  text-align: center;
}

.metric-label {
  display: block;
  font-size: 0.8rem;
  color: #6c757d;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 0.25rem;
}

.metric-value {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  color: #2c3e50;
}

.metric-value.positive {
  color: #28a745;
}

.metric-value.negative {
  color: #dc3545;
}

/* Wizard Content */
.wizard-content {
  min-height: 500px;
  margin-bottom: 2rem;
}

.wizard-step {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 2rem;
}

.step-header {
  text-align: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e9ecef;
}

.step-header h2 {
  margin: 0 0 0.5rem 0;
  color: #2c3e50;
  font-size: 1.75rem;
}

.step-header p {
  margin: 0;
  color: #6c757d;
  font-size: 1.1rem;
}

.step-transition-enter-active,
.step-transition-leave-active {
  transition: all 0.3s ease;
}

.step-transition-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.step-transition-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

/* Form Styles */
.inputs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.input-section {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  border: 1px solid #e9ecef;
}

.input-section h3 {
  margin: 0 0 1rem 0;
  color: #495057;
  font-size: 1.1rem;
  font-weight: 600;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 0.5rem;
}

.input-group {
  margin-bottom: 1rem;
}

.input-group:last-child {
  margin-bottom: 0;
}

.input-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-prefix {
  position: absolute;
  left: 12px;
  color: #6c757d;
  font-weight: 600;
  z-index: 1;
}

.form-input,
.form-select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.input-wrapper .form-input {
  padding-left: 2rem;
}

.input-help {
  display: block;
  margin-top: 0.25rem;
  color: #6c757d;
  font-size: 0.8rem;
  line-height: 1.3;
}

.input-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
}

/* Tax Analysis */
.tax-analysis-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
}

.tax-card {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  border: 2px solid transparent;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.tax-card.current {
  border-color: #007bff;
  background: linear-gradient(to bottom, #f8fcff, white);
}

.tax-card.retirement {
  border-color: #6f42c1;
  background: linear-gradient(to bottom, #f8f7ff, white);
}

.tax-card h3 {
  margin: 0 0 1rem 0;
  color: #2c3e50;
  font-size: 1.2rem;
}

.tax-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.tax-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: rgba(248, 249, 250, 0.8);
  border-radius: 4px;
}

.tax-label {
  color: #6c757d;
  font-size: 0.9rem;
}

.tax-value {
  font-weight: 600;
  color: #2c3e50;
}

/* Conversion Preview */
.conversion-preview {
  background: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 8px;
  padding: 1.5rem;
}

.conversion-preview h3 {
  margin: 0 0 1rem 0;
  color: #1565c0;
  font-size: 1.1rem;
}

.preview-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.preview-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: white;
  border-radius: 4px;
  border: 1px solid #bbdefb;
}

.preview-label {
  color: #1976d2;
  font-size: 0.9rem;
}

.preview-value {
  font-weight: 600;
  color: #0d47a1;
}

/* Results */
.results-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.recommendation-card {
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.recommendation-header {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.recommendation-icon {
  font-size: 3rem;
}

.recommendation-text h3 {
  margin: 0 0 0.5rem 0;
  color: #2c3e50;
  font-size: 1.5rem;
}

.recommendation-text p {
  margin: 0;
  color: #6c757d;
  font-size: 1rem;
  line-height: 1.4;
}

.recommendation-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 6px;
}

.detail-label {
  color: #495057;
  font-size: 0.9rem;
}

.detail-value {
  font-weight: 600;
  color: #2c3e50;
  font-size: 1rem;
}

.detail-value.positive {
  color: #28a745;
}

.detail-value.negative {
  color: #dc3545;
}

/* Action Items */
.action-items h4 {
  margin: 0 0 1rem 0;
  color: #495057;
  font-size: 1.2rem;
}

.action-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.action-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #dee2e6;
}

.action-number {
  background: #007bff;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  flex-shrink: 0;
}

.action-text {
  line-height: 1.4;
  color: #495057;
}

/* Navigation */
.wizard-navigation {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.nav-spacer {
  flex: 1;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
  transform: translateY(-1px);
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #545b62;
  transform: translateY(-1px);
}

.btn-success {
  background: #28a745;
  color: white;
}

.btn-success:hover {
  background: #218838;
  transform: translateY(-1px);
}

/* Responsive */
@media (max-width: 768px) {
  .roth-conversion-wizard {
    padding: 0.5rem;
  }
  
  .wizard-header {
    padding: 1rem;
  }
  
  .header-content h1 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }
  
  .step-indicator {
    gap: 1rem;
  }
  
  .step-dot {
    padding: 0.5rem;
  }
  
  .summary-card {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .key-metrics {
    gap: 1rem;
  }
  
  .wizard-step {
    padding: 1rem;
  }
  
  .inputs-grid {
    grid-template-columns: 1fr;
  }
  
  .input-row {
    grid-template-columns: 1fr;
  }
  
  .tax-analysis-grid {
    grid-template-columns: 1fr;
  }
  
  .preview-metrics {
    grid-template-columns: 1fr;
  }
  
  .recommendation-header {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }
  
  .wizard-navigation {
    padding: 1rem;
  }
}
</style>